import { useEffect, useState } from 'react';

/**
 * Subscribers to the shared second-tick, and the one interval serving them.
 *
 * A run detail page can hold many live rows — one per running step, plus the HITL panel — and a
 * timer per row is one timer too many: they would also drift apart, so two rows a second out of
 * step would show different elapsed values for the same instant. One interval exists while at least
 * one subscriber is mounted and is cleared when the last one leaves.
 */
const tickSubscribers = new Set<(now: number) => void>();
let tickTimer: ReturnType<typeof setInterval> | null = null;

function subscribeToTick(onTick: (now: number) => void): () => void {
  tickSubscribers.add(onTick);
  if (tickTimer === null) {
    tickTimer = setInterval(() => {
      const now = Date.now();
      for (const subscriber of tickSubscribers) subscriber(now);
    }, 1_000);
  }
  return () => {
    tickSubscribers.delete(onTick);
    if (tickSubscribers.size === 0 && tickTimer !== null) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  };
}

/**
 * A `Date.now()` value that advances once a second while `enabled`, for anything that has to keep
 * counting between server updates — a HITL request's expiry, a step's elapsed time. When disabled it
 * returns a frozen reading and holds no subscription, so a page full of finished cases costs nothing.
 *
 * Enabling re-reads the clock immediately rather than waiting up to a second for the first tick: a
 * step that has been running for three minutes must not render as `2m 59s` for its first frame.
 */
export function useTickingNow(enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    setNow(Date.now());
    return subscribeToTick(setNow);
  }, [enabled]);
  return now;
}

/**
 * How long something has been running, in whole units: `45s`, `1m 12s`, `1h 04m`.
 *
 * Deliberately not `formatDuration`, which is built for a finished measurement and shows a tenth of
 * a second — a tenth that never changes on a once-a-second tick reads like a frozen display. Hours
 * appear because a case can legitimately sit at the one-hour cap.
 */
export function formatElapsed(elapsedMs: number): string {
  const totalSeconds = Math.floor(Math.max(0, elapsedMs) / 1_000);
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3_600);
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

/**
 * The same elapsed time as a stopwatch reads it: `00:01`, `23:45`, `1:04:12`.
 *
 * Beside `formatElapsed` rather than replacing it, because the two are read differently. A figure
 * sitting in a line of other figures wants a unit to say what it is (`1m 12s`); a clock that is
 * visibly counting does not, and a fixed-width `mm:ss` is what makes a once-a-second tick read as
 * running rather than as a value that happens to have changed. The seconds are always padded for the
 * same reason — a field that narrows from `10` to `9` reads as a glitch.
 *
 * Hours drop the leading zero on the hour field alone (`1:04:12`), the way every stopwatch does.
 */
export function formatStopwatch(elapsedMs: number): string {
  const totalSeconds = Math.floor(Math.max(0, elapsedMs) / 1_000);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3_600);
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${seconds}`;
  }
  return `${String(minutes).padStart(2, '0')}:${seconds}`;
}

/**
 * Milliseconds a step has been running, or null when it cannot be stated honestly.
 *
 * `start` is the worker's own clock reading (`progress.json`), compared here against the browser's,
 * so a machine whose clock sits ahead of the runner's would otherwise render a negative or absurd
 * age. A start in the future is refused outright rather than clamped to zero: showing `0s` for a
 * step that has genuinely been running for minutes is a confident lie, while showing nothing is
 * merely the absence of a number. An unparsable or non-positive `start` is refused for the same
 * reason.
 */
export function elapsedSinceStart(
  start: number | string | null | undefined,
  now: number,
): number | null {
  if (!isUsableStart(start)) return null;
  const elapsed = now - Number(start);
  return elapsed < 0 ? null : elapsed;
}

/**
 * Whether a `start` could yield an elapsed value at all — a finite, positive number, or a string
 * that parses to one.
 *
 * Separate from `elapsedSinceStart` because the decision to run a clock has to be made without
 * reading one: `useTickingNow(step.start != null)` would subscribe a row whose `start` is `0` or
 * `"n/a"`, keeping the shared second-tick alive for a row that can never show a number.
 */
export function isUsableStart(
  start: number | string | null | undefined,
): boolean {
  if (start === null || start === undefined || start === '') return false;
  const startMs = Number(start);
  return Number.isFinite(startMs) && startMs > 0;
}
